import { useEffect, useRef } from 'react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type { BoundingBox } from './types';
import type { Polygon, MultiPolygon } from 'geojson';
import DrawRectangleDragMode from './DrawRectangleDragMode';
import DirectSelectRectangleMode from './DirectSelectRectangleMode';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

/** ID de la capa de límite administrativo */
const ADMIN_BOUNDARY_SOURCE_ID = 'admin-boundary-source';
const ADMIN_BOUNDARY_FILL_LAYER_ID = 'admin-boundary-fill';
const ADMIN_BOUNDARY_LINE_LAYER_ID = 'admin-boundary-line';

/**
 * Props para BoundingBoxDrawer
 */
interface BoundingBoxDrawerProps {
  map: MapLibreMap | null;
  initialBounds: BoundingBox;
  /** Límite administrativo personalizado (polígono real) */
  customBounds?: Polygon | MultiPolygon | null;
  onBoundsChange: (bounds: BoundingBox) => void;
  onDrawerReady?: (drawer: MapboxDraw) => void;
}

/**
 * BoundingBoxDrawer - Componente para dibujar y editar bounding boxes en el mapa
 *
 * @remarks
 * Permite al usuario dibujar un rectángulo interactivo en el mapa que representa
 * el bounding box del proyecto. El rectángulo se puede:
 * - Dibujar desde cero
 * - Editar arrastrando los vértices
 * - Mover completamente
 * - Redimensionar
 *
 * @param map - Instancia del mapa MapLibre
 * @param initialBounds - Bounding box inicial (si existe, se dibuja automáticamente)
 * @param onBoundsChange - Callback cuando cambian las coordenadas
 */
export function BoundingBoxDrawer({
  map,
  initialBounds,
  customBounds,
  onBoundsChange,
  onDrawerReady,
}: BoundingBoxDrawerProps): null {
  const drawRef = useRef<MapboxDraw | null>(null);
  const onBoundsChangeRef = useRef(onBoundsChange);
  const onDrawerReadyRef = useRef(onDrawerReady);
  const handlersRegisteredRef = useRef(false);
  const initialBoundsDrawnRef = useRef(false);
  const adminBoundaryAddedRef = useRef(false);

  // Keep refs updated
  useEffect(() => {
    onBoundsChangeRef.current = onBoundsChange;
    onDrawerReadyRef.current = onDrawerReady;
  }, [onBoundsChange, onDrawerReady]);

  useEffect(() => {
    if (!map) return;

    // Inicializar MapboxDraw solo una vez
    if (!drawRef.current) {
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: false,
          line_string: false,
          point: false,
          trash: true,
        },
        modes: {
          ...MapboxDraw.modes,
          draw_rectangle_drag: DrawRectangleDragMode,
          direct_select: DirectSelectRectangleMode, // REEMPLAZA el modo original para preservar ángulos de 90°
        },
        styles: [
          // Polígono relleno
          {
            id: 'gl-draw-polygon-fill',
            type: 'fill',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#2FA4B5',
              'fill-opacity': 0.15,
            },
          },
          // Borde del polígono
          {
            id: 'gl-draw-polygon-stroke',
            type: 'line',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'line-color': '#2FA4B5',
              'line-width': 2,
            },
          },
          // Vértices - todos los vértices (4 esquinas)
          {
            id: 'gl-draw-polygon-vertex',
            type: 'circle',
            filter: [
              'all',
              ['==', 'meta', 'vertex'],
              ['==', '$type', 'Point'],
            ],
            paint: {
              'circle-radius': 6,
              'circle-color': '#2FA4B5',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#ffffff',
            },
          },
        ],
      });

      // @ts-ignore - MapboxDraw es compatible con MapLibre
      map.addControl(draw, 'top-right');
      drawRef.current = draw;

      // Notificar que el drawer está listo
      if (onDrawerReadyRef.current) {
        onDrawerReadyRef.current(draw);
      }
    }

    // Registrar eventos solo una vez
    if (drawRef.current && !handlersRegisteredRef.current) {
      const draw = drawRef.current;

      console.log('📝 Registrando event handlers de Draw (solo una vez)');

      // Eventos de cambio
      const handleUpdate = () => {
        try {
          const data = draw.getAll();
          console.log(`📊 [handleUpdate] Total features en Draw: ${data.features.length}`);

          if (data.features.length > 0) {
            // ⚠️ Si hay múltiples rectángulos, loggear advertencia pero continuar con el primero
            if (data.features.length > 1) {
              console.warn(`⚠️ DUPLICACIÓN DETECTADA: ${data.features.length} rectángulos en Draw`);
              data.features.forEach((f: any, i: number) => {
                console.warn(`  - Feature ${i + 1}: ID=${f.id}, Type=${f.geometry.type}`);
              });
            }

            const feature = data.features[0];
            // @ts-ignore
            const coords = feature.geometry?.coordinates?.[0];

            // Validar que tenemos coordenadas válidas
            if (!coords || !Array.isArray(coords) || coords.length < 4) {
              console.warn('⚠️ Coordenadas inválidas en el feature');
              return;
            }

            // Calcular el bounding box desde las coordenadas del polígono
            const lngs = coords.map((c: number[]) => c[0]);
            const lats = coords.map((c: number[]) => c[1]);

            // Crear siempre un nuevo objeto para que React detecte el cambio
            const newBounds: BoundingBox = {
              minLng: Math.min(...lngs),
              minLat: Math.min(...lats),
              maxLng: Math.max(...lngs),
              maxLat: Math.max(...lats),
            };

            console.log('🔄 Bounds actualizados:', newBounds);

            if (onBoundsChangeRef.current) {
              onBoundsChangeRef.current(newBounds);
            }
          } else {
            console.log('📍 No hay features dibujados');
          }
        } catch (error) {
          console.error('❌ Error actualizando bounds:', error);
        }
      };

      const handleDelete = () => {
        // Si se elimina el rectángulo, resetear a bounds continentales
        if (onBoundsChangeRef.current) {
          onBoundsChangeRef.current({
            minLng: -170,
            minLat: -60,
            maxLng: -30,
            maxLat: 75,
          });
        }
      };

      map.on('draw.create', () => {
        console.log('🎨 Evento draw.create disparado');
        handleUpdate();
      });
      map.on('draw.update', () => {
        console.log('🔄 Evento draw.update disparado');
        handleUpdate();
      });
      map.on('draw.delete', () => {
        console.log('🗑️ Evento draw.delete disparado');
        handleDelete();
      });

      handlersRegisteredRef.current = true;
    }

    // Cleanup solo cuando el componente se desmonta
    return undefined;
  }, [map]);

  // Efecto para dibujar el rectángulo inicial cuando se edita un proyecto existente
  useEffect(() => {
    if (!map || !drawRef.current || initialBoundsDrawnRef.current) return;

    // Verificar si los initialBounds NO son los continentales por defecto
    const isContinental =
      initialBounds.minLng === -170 &&
      initialBounds.minLat === -60 &&
      initialBounds.maxLng === -30 &&
      initialBounds.maxLat === 75;

    if (isContinental) {
      console.log('📍 Bounds continentales detectados, no dibujando rectángulo inicial');
      return;
    }

    // Si hay bounds válidos, dibujar el rectángulo
    console.log('🎨 Dibujando rectángulo inicial con bounds:', initialBounds);

    try {
      const draw = drawRef.current;

      // Crear el polígono a partir de los bounds
      const rectangle = {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [initialBounds.minLng, initialBounds.minLat], // SW
              [initialBounds.maxLng, initialBounds.minLat], // SE
              [initialBounds.maxLng, initialBounds.maxLat], // NE
              [initialBounds.minLng, initialBounds.maxLat], // NW
              [initialBounds.minLng, initialBounds.minLat], // Cerrar
            ],
          ],
        },
      };

      // Limpiar cualquier rectángulo previo
      const existingFeatures = draw.getAll();
      if (existingFeatures.features.length > 0) {
        console.log('🧹 Limpiando rectángulos previos');
        draw.deleteAll();
      }

      // Agregar el rectángulo
      // @ts-ignore
      const addedIds = draw.add(rectangle);
      console.log('✅ Rectángulo inicial dibujado con ID:', addedIds);

      // IMPORTANTE: Seleccionar el rectángulo y activar modo de edición con ángulos de 90°
      if (addedIds && addedIds.length > 0) {
        const rectangleId = addedIds[0];
        console.log('🎯 Seleccionando rectángulo para edición:', rectangleId);

        // Pequeño delay para asegurar que el rectángulo esté completamente agregado
        setTimeout(() => {
          // Cambiar al modo direct_select (que usará DirectSelectRectangleMode)
          // que mantiene ángulos de 90°
          // @ts-ignore
          draw.changeMode('direct_select', { featureId: rectangleId });
          console.log('✅ Modo de edición rectangular activado (ángulos de 90° preservados)');
        }, 100);
      }

      // Hacer zoom al rectángulo
      map.fitBounds(
        [
          [initialBounds.minLng, initialBounds.minLat],
          [initialBounds.maxLng, initialBounds.maxLat],
        ],
        {
          padding: 50,
          duration: 1000,
        }
      );

      initialBoundsDrawnRef.current = true;
    } catch (error) {
      console.error('❌ Error dibujando rectángulo inicial:', error);
    }
  }, [map, initialBounds]);

  // Efecto para mostrar/ocultar el límite administrativo (polígono real)
  useEffect(() => {
    if (!map) return;

    // Helper para limpiar layers y source del límite administrativo
    const cleanupAdminBoundary = () => {
      try {
        if (map.getLayer(ADMIN_BOUNDARY_LINE_LAYER_ID)) {
          map.removeLayer(ADMIN_BOUNDARY_LINE_LAYER_ID);
        }
        if (map.getLayer(ADMIN_BOUNDARY_FILL_LAYER_ID)) {
          map.removeLayer(ADMIN_BOUNDARY_FILL_LAYER_ID);
        }
        if (map.getSource(ADMIN_BOUNDARY_SOURCE_ID)) {
          map.removeSource(ADMIN_BOUNDARY_SOURCE_ID);
        }
        adminBoundaryAddedRef.current = false;
      } catch (e) {
        // Ignorar errores de limpieza
      }
    };

    // Si hay customBounds, mostrar el polígono real
    if (customBounds) {
      console.log('🗺️ Mostrando límite administrativo (polígono real)');

      // Ocultar el rectángulo del drawer si existe
      if (drawRef.current) {
        const existingFeatures = drawRef.current.getAll();
        if (existingFeatures.features.length > 0) {
          drawRef.current.deleteAll();
        }
      }

      // Limpiar cualquier límite anterior
      cleanupAdminBoundary();

      // Agregar source con el polígono
      map.addSource(ADMIN_BOUNDARY_SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: customBounds,
        },
      });

      // Agregar capa de relleno
      map.addLayer({
        id: ADMIN_BOUNDARY_FILL_LAYER_ID,
        type: 'fill',
        source: ADMIN_BOUNDARY_SOURCE_ID,
        paint: {
          'fill-color': '#2FA4B5',
          'fill-opacity': 0.15,
        },
      });

      // Agregar capa de línea (borde)
      map.addLayer({
        id: ADMIN_BOUNDARY_LINE_LAYER_ID,
        type: 'line',
        source: ADMIN_BOUNDARY_SOURCE_ID,
        paint: {
          'line-color': '#2FA4B5',
          'line-width': 2,
        },
      });

      adminBoundaryAddedRef.current = true;

      // Hacer zoom al polígono
      // Calcular bbox del polígono
      let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
      const processCoords = (coords: number[][]) => {
        for (const [lng, lat] of coords) {
          minLng = Math.min(minLng, lng);
          minLat = Math.min(minLat, lat);
          maxLng = Math.max(maxLng, lng);
          maxLat = Math.max(maxLat, lat);
        }
      };

      if (customBounds.type === 'Polygon') {
        processCoords(customBounds.coordinates[0]);
      } else if (customBounds.type === 'MultiPolygon') {
        for (const polygon of customBounds.coordinates) {
          processCoords(polygon[0]);
        }
      }

      if (minLng !== Infinity) {
        map.fitBounds(
          [[minLng, minLat], [maxLng, maxLat]],
          { padding: 50, duration: 1000 }
        );
      }

      console.log('✅ Límite administrativo agregado al mapa');
    } else {
      // Si no hay customBounds, limpiar el límite administrativo
      cleanupAdminBoundary();
    }

    // Cleanup al desmontar o cambiar customBounds
    return () => {
      cleanupAdminBoundary();
    };
  }, [map, customBounds]);

  return null;
}
